// pages/master-data/Pit/MasterPitPage.jsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import { Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, IconButton, Box } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { checkIsUsedInRouteTrip } from '../../../stores/routeTripStore';
import { usePoolList, deletePool } from '../../../stores/poolStore';
import { useCompanyList } from '../../../stores/companyStore';
import { useCountryList } from '../../../stores/countryStore';
import { useAreaList } from '../../../stores/areaStore';

import ActionMenu from '../../../components/ActionMenu';

import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';

import { fleetApi } from '../../../api/axiosClient';
import { normalizeSearchFieldsConfig } from '../../../utils/searchHelper';
import MasterPitAddEdit from './MasterPitAddEdit';
import { useLanguage } from '../../../utils/i18n';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

function MasterPitPageContent() {
  const navigate = useNavigate();
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const notify = useNotifier();
  const { t } = useLanguage();
  const { companyOptions } = useCompanyList();

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  const { data: countryList = [] } = useCountryList(0, 1000, '');
  const { data: areaList = [] } = useAreaList(0, 1000, '');

  const countryOptions = useMemo(
    () =>
      (countryList || []).map((v) => ({
        value: v?.id ?? v?.value ?? null,
        label: v?.name ?? v?.country ?? String(v?.id ?? v?.value ?? '')
      })),
    [countryList]
  );

  const areaOptions = useMemo(
    () =>
      (areaList || []).map((v) => ({
        value: v?.id ?? v?.value ?? null,
        label: v?.name ?? v?.area_name ?? String(v?.id ?? v?.value ?? '')
      })),
    [areaList]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const companyFilterColumns = useMemo(() => {
    let storedCompanyId = null;
    try {
      if (typeof window !== 'undefined') storedCompanyId = localStorage.getItem('companyId');
    } catch {
      storedCompanyId = null;
    }

    if (selectedCompanies && selectedCompanies.length > 0) {
      const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
      if (hasAll) return [];
      const values = selectedCompanies.map((s) => String(s.value));
      if (values.length === 1) {
        return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] }];
      } else {
        return [{ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') }];
      }
    }

    if (storedCompanyId && String(storedCompanyId) !== 'all') {
      return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: String(storedCompanyId) }];
    }
    return [];
  }, [selectedCompanies]);

  const [searchField, setSearchField] = useState('name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [, setSearchExtraColumns] = useState([]);

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'name', label: t('masterPit.name', 'Pit Name') },
      { value: 'pool_code', label: t('masterPit.code', 'Pit Code') },
      { value: 'address', label: t('masterPit.address', 'Address') },
      { value: 'remark', label: t('masterPit.remark', 'Remark') },
      { value: 'min_speed', label: t('masterPit.minSpeed', 'Min Speed') },
      { value: 'max_speed', label: t('masterPit.maxSpeed', 'Max Speed') }
    ],
    [t]
  );

  const joinDefs = useMemo(
    () => ({
      company: { name: 'm_company', column_results: ['name'] },
      country: { name: 'm_country', column_results: ['name'] },
      area: { name: 'm_area', column_results: ['name'] }
    }),
    []
  );

  const searchFieldsConfig = useMemo(() => normalizeSearchFieldsConfig(searchFieldOptions), [searchFieldOptions]);
  const filterCompanyIds = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchRaw = searchField === 'all' ? '' : searchRaw;

  const {
    data: poolsFromHook,
    isLoading: hookLoading,
    totalRecord: hookTotal
  } = usePoolList(paginationModel.page, paginationModel.pageSize, hookSearchField, hookSearchRaw, filterCompanyIds);

  const [localPools, setLocalPools] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();
    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;

        let fetchFilters = [];
        if (searchValue) {
          fetchFilters = [
            { name: 'name', value: searchValue },
            { name: 'pool_code', value: searchValue },
            { name: 'address', value: searchValue },
            { name: 'remark', value: searchValue },
            { name: 'min_speed', value: searchValue },
            { name: 'max_speed', value: searchValue },
            { name: 'name', table_name: 'm_company', value: searchValue }
          ];
        }

        const payload = {
          paging: { start, length },
          orders: { columns: ['name'], ascending: true },
          joins: [
            { name: 'company', column_results: ['name'] },
            { name: 'country', column_results: ['name'] },
            { name: 'area', column_results: ['name'] }
          ],
          columns: companyFilterColumns
        };

        if (fetchFilters.length > 0) {
          payload.filters = fetchFilters;
        }

        const resp = await fleetApi.post('/pool/search' + getTzParam(), payload, { signal: controller.signal });
        const data = resp?.data ?? null;
        const rows = Array.isArray(data) ? data : (data?.data ?? []);
        const total = Array.isArray(data) ? rows.length : (data?.totalRecord ?? 0);

        setLocalPools(rows);
        setLocalTotal(total);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('pool search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel, companyFilterColumns, searchFieldsConfig, joinDefs]);

  const rows = searchField === 'all' ? localPools : poolsFromHook || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecords = searchField === 'all' ? localTotal : hookTotal;

  useEffect(() => {
    const t = setTimeout(() => {
      const v = String(searchRaw ?? '').trim();

      if (!v) {
        setSearchExtraColumns([...companyFilterColumns]);
        setSearchValue('');
        setPaginationModel((p) => ({ ...p, page: 0 }));
        return;
      }

      if (searchField === 'all') {
        setSearchExtraColumns([...companyFilterColumns]);
        setSearchValue(v);
        setPaginationModel((p) => ({ ...p, page: 0 }));
        return;
      }

      const searchCol = { name: searchField, logic_operator: 'like', operator: 'AND', value: v };
      setSearchExtraColumns([...companyFilterColumns, searchCol]);
      setSearchValue(v);
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(t);
  }, [searchRaw, searchField, companyFilterColumns]);

  // handlers
  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch {
      console.log('eror');
    }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [warningOpen, setWarningOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const handleDelete = async (id, itemName) => {
    const { isUsed, routeNames } = await checkIsUsedInRouteTrip(id, 'pool');
    if (isUsed) {
      setWarningMessage(
        <Box>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Data "<strong>{itemName}</strong>" is not allowed to be deleted because it is registered in the following route(s):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, maxHeight: 150, overflowY: 'auto', p: 0.5 }}>
            {routeNames.map((r, i) => (
              <Chip key={i} label={r} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600, bgcolor: 'error.50' }} />
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary">
            If you wish to delete it, please update the route first.
          </Typography>
        </Box>
      );
      setWarningOpen(true);
      return;
    }

    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deletePool(confirmTargetId);
      notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('notify.deleteFailed', 'Failed to delete'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const handleSearch = ({ field, value }) => {
    setSearchField(field);
    setSearchRaw(value ?? '');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const columns = [
    {
      field: 'company',
      headerName: t('masterPit.company', 'Company'),
      flex: 1.2,
      valueGetter: (params, row) => row?.join?.company_name || '-'
    },
    { field: 'name', headerName: t('masterPit.name', 'Pit Name'), flex: 2 },
    { field: 'pool_code', headerName: t('masterPit.code', 'Pit Code'), flex: 1 },
    { field: 'address', headerName: t('masterPit.address', 'Address'), flex: 2 },
    {
      field: 'min_speed',
      headerName: t('masterPit.minSpeed', 'Min Speed'),
      flex: 1,
      valueGetter: (value, params) => (params.min_speed ? `${params.min_speed} km/h` : '-')
    },
    {
      field: 'max_speed',
      headerName: t('masterPit.maxSpeed', 'Max Speed'),
      flex: 1,
      valueGetter: (value, params) => (params.max_speed ? `${params.max_speed} km/h` : '-')
    },
    { field: 'remark', headerName: t('masterPit.remark', 'Remark'), flex: 1 },
    {
      field: 'status',
      headerName: t('masterPit.status', 'Status'),
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (value, row) => {
        const st = row?.status ?? row?.is_active ?? null;
        if (st === null || st === undefined) return '-';
        if (typeof st === 'boolean') return st ? 'Active' : 'Not Active';
        return String(st);
      },
      renderCell: (params) => {
        const st = params.row?.status ?? params.row?.is_active ?? null;
        if (st === null || st === undefined) return '-';
        if (typeof st === 'boolean') {
          return st ? (
            <Chip label={t('general.active', 'Active')} color="success" variant="outlined" size="small" />
          ) : (
            <Chip label={t('general.notActive', 'Not Active')} color="error" variant="outlined" size="small" />
          );
        }
        return <Chip label={String(st)} variant="outlined" size="small" />;
      }
    },
    { field: 'geom', headerName: t('polygonType.geom', 'Geom'), hide: true },
    { field: 'centroid_lat', headerName: t('polygonType.centroidLatitude', 'Centroid Latitude'), hide: true },
    { field: 'centroid_lon', headerName: t('polygonType.centroidLongitude', 'Centroid Longitude'), hide: true },

    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('masterCompany.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterCompany.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterCompany.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterCompany.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.name)} mode="inline" />
      )
    }
  ];

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      const fetchColumns = [...companyFilterColumns];

      if (searchField !== 'all' && searchRaw) {
        const opt = searchFieldOptions.find((o) => o.value === searchField);
        if (opt) {
          const searchCol = {
            name: opt.fieldName || searchField,
            logic_operator: 'like',
            operator: 'AND',
            value: searchRaw
          };
          if (opt.tableName) {
            searchCol.table_name = opt.tableName;
          }
          fetchColumns.push(searchCol);
        }
      }

      let fetchFilters = [];
      if (searchField === 'all' && searchRaw) {
        fetchFilters = [
          { name: 'name', value: searchRaw },
          { name: 'pool_code', value: searchRaw },
          { name: 'name', table_name: 'm_country', value: searchRaw },
          { name: 'name', table_name: 'm_area', value: searchRaw }
        ];
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        joins: [
          { name: 'company', column_results: ['name'] },
          { name: 'country', column_results: ['name'] },
          { name: 'area', column_results: ['name'] }
        ],
        columns: fetchColumns
      };

      if (fetchFilters.length > 0) {
        payload.filters = fetchFilters;
      }

      const resp = await fleetApi.post('/pool/search', payload);
      const data = resp?.data?.data || resp?.data || [];
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  return (
    <div>
      <DataTable
        title={t('masterPit.title', 'Master PIT')}
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        // search controls
        searchQuery={searchRaw}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setSearchExtraColumns([...companyFilterColumns]);
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('general.searchByName', 'Search by Name')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        onFetchAllForExport={handleFetchAllForExport}
      />

      <MasterPitAddEdit
        open={openDialog}
        id={editingId}
        companyOptions={companyOptionsNormalized}
        countryOptions={countryOptions}
        areaOptions={areaOptions}
        onClose={handleDialogClose}
        onSuccess={(msg) => {
          setPaginationModel((p) => ({ ...p }));
          handleDialogClose();
        }}
        onError={(msg) => {
          notify({ message: msg || t('notify.operationFailed', 'Operation failed'), severity: 'error' });
        }}
      />

      <Dialog
        open={warningOpen}
        onClose={() => setWarningOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, p: 0, overflow: 'visible', boxShadow: '0 10px 30px rgba(8,14,30,0.12)' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 152, 0, 0.08))',
                border: '1px solid rgba(255,152,0,0.18)'
              }}
            >
              <WarningAmberIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            </Box>
            <DialogTitle sx={{ p: 0, fontWeight: 700 }}>Action Denied</DialogTitle>
          </Box>
          <IconButton onClick={() => setWarningOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent dividers sx={{ pt: 2, pb: 2 }}>
          {typeof warningMessage === 'string' ? (
            <Typography variant="body1" sx={{ mb: 1.25 }}>
              {warningMessage}
            </Typography>
          ) : (
            warningMessage
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setWarningOpen(false);
              navigate('/master-route?name=master-route');
            }}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              fontWeight: 600
            }}
          >
            Master Route
          </Button>
          <Button
            variant="contained"
            onClick={() => setWarningOpen(false)}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              fontWeight: 600,
              boxShadow: '0 6px 18px rgba(0,0,0,0.08)'
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />
    </div>
  );
}

export default function MasterPitPage() {
  return (
    <>
      <MasterPitPageContent />
    </>
  );
}
